import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { loadDecompressHandlers } from "@mcap/support";
import { BlobReadable } from "@mcap/browser";
import { McapIndexedReader } from "@mcap/core";
import {
  Card,
  Row,
  Col,
  Button,
  Slider,
  Select,
  Input,
  Statistic,
  Tag,
  Space,
  Collapse,
  Table,
  Typography,
  Tabs,
  Tooltip,
  InputNumber,
  Spin,
  Empty,
  Divider,
  Badge,
  Switch,
  Descriptions,
} from "antd";
import {
  PlayCircleOutlined,
  PauseCircleOutlined,
  StepForwardOutlined,
  StepBackwardOutlined,
  FastBackwardOutlined,
  ReloadOutlined,
  FilterOutlined,
  SearchOutlined,
  DownloadOutlined,
  FileTextOutlined,
  CloudUploadOutlined,
  LinkOutlined,
  DatabaseOutlined,
  InfoCircleOutlined,
  ClockCircleOutlined,
  CompressOutlined,
  PaperClipOutlined,
  TagsOutlined,
} from "@ant-design/icons";
import protobuf from "protobufjs";
import { FileDescriptorSet } from "protobufjs/ext/descriptor";
import { mcapLogsService } from "features/robot-logs/services/mcapLogsService";
import { useAuth } from "shared/contexts/AuthProvider";
import { formatEpochNs } from "shared/utils/datetime";

const { Panel } = Collapse;
const { Option } = Select;
const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

function nsToISOString(nsValue) {
  if (nsValue == null) return "—";
  try {
    return formatEpochNs(nsValue, nsValue.toString());
  } catch {
    return nsValue.toString();
  }
}

function nsDuration(startNs, endNs) {
  if (startNs == null || endNs == null) return "—";
  try {
    // Drop last 6 digits of the ns string to get milliseconds (integer, no BigInt needed)
    const toMs = (ns) => {
      const s = ns.toString();
      return parseInt(s.length > 6 ? s.slice(0, s.length - 6) : "0", 10);
    };
    const startMs = toMs(startNs);
    const endMs = toMs(endNs);
    const totalMs = Math.abs(endMs - startMs);
    const ms = totalMs % 1000;
    const totalSec = Math.floor(totalMs / 1000);
    const sec = totalSec % 60;
    const totalMin = Math.floor(totalSec / 60);
    const min = totalMin % 60;
    const hrs = Math.floor(totalMin / 60);
    if (hrs > 0) return `${hrs}h ${min}m ${sec}s`;
    if (min > 0) return `${min}m ${sec}s ${ms}ms`;
    if (sec > 0) return `${sec}s ${ms}ms`;
    return `${ms}ms`;
  } catch {
    return "—";
  }
}

function bytesToHex(u8, maxBytes = 64) {
  if (!(u8 instanceof Uint8Array)) return "";
  const n = Math.min(u8.length, maxBytes);
  let s = "";
  for (let i = 0; i < n; i++) s += u8[i].toString(16).padStart(2, "0");
  return u8.length > maxBytes ? `${s}… (+${u8.length - maxBytes} bytes)` : s;
}

function safeBigIntToString(_k, v) {
  return typeof v === "bigint" ? v.toString() : v;
}

function decodeJsonPayload(dataU8) {
  try {
    const text = new TextDecoder("utf-8", { fatal: false }).decode(dataU8);
    return { kind: "json", value: JSON.parse(text) };
  } catch {
    return null;
  }
}

/**
 * Build a protobufjs Root from a binary FileDescriptorSet.
 * Assumes schema.data contains FileDescriptorSet bytes.
 */
function rootFromFileDescriptorSet(schemaDataU8) {
  // FileDescriptorSet.decode expects a Uint8Array
  const fds = FileDescriptorSet.decode(schemaDataU8);
  // protobuf.Root.fromDescriptor exists in protobufjs (used by grpc/proto-loader as well)
  // If your bundler complains, ensure protobufjs is up to date.
  const root = protobuf.Root.fromDescriptor(fds);
  return root;
}

function decodeProtobufPayload({ dataU8, schema, cache }) {
  // Cache per schema.id to avoid rebuilding reflection repeatedly
  let entry = cache.get(schema.id);
  if (!entry) {
    const root = rootFromFileDescriptorSet(schema.data);
    // MCAP schema.name should be the fully qualified message name (common practice).
    // Example: "mypackage.MyMessage"
    const type = root.lookupType(schema.name);
    entry = { root, type };
    cache.set(schema.id, entry);
  }

  const msg = entry.type.decode(dataU8);
  const obj = entry.type.toObject(msg, {
    longs: String,
    enums: String,
    bytes: String,
    defaults: false,
    oneofs: true,
  });
  return { kind: "protobuf", value: obj };
}

/**
 * Attempt to decode schema data into a human-readable form.
 * Returns { kind: 'json' | 'text' | 'protobuf_fds' | 'hex' | 'error', value }
 */
function decodeSchemaData(schema) {
  const enc = (schema.encoding || "").toLowerCase();
  const data = schema.data;

  if (!data || data.length === 0) return null;

  // Text-based encodings: JSON Schema, ROS message definitions, OMG IDL
  const textEncodings = ["jsonschema", "ros1msg", "ros2msg", "ros2idl", "omgidl"];
  if (textEncodings.includes(enc)) {
    try {
      const text = new TextDecoder("utf-8", { fatal: true }).decode(data);
      if (enc === "jsonschema") {
        try {
          return { kind: "json", value: JSON.parse(text) };
        } catch {
          return { kind: "text", value: text };
        }
      }
      return { kind: "text", value: text };
    } catch {
      // fall through
    }
  }

  // Protobuf FileDescriptorSet
  if (enc === "protobuf") {
    try {
      const fds = FileDescriptorSet.decode(data);
      const obj = FileDescriptorSet.toObject(fds, {
        longs: String,
        enums: String,
        bytes: String,
        defaults: false,
        oneofs: true,
      });
      return { kind: "protobuf_fds", value: obj };
    } catch (e) {
      return { kind: "error", value: e?.message ?? String(e) };
    }
  }

  // Best-effort UTF-8 text for any other encoding
  try {
    const text = new TextDecoder("utf-8", { fatal: true }).decode(data);
    // Only show as text if it looks reasonably printable
    const printable = text.split("").filter((c) => c.charCodeAt(0) >= 32 || c === "\n" || c === "\t").length;
    if (printable / text.length > 0.85) {
      return { kind: "text", value: text };
    }
  } catch {
    // fall through
  }

  return { kind: "hex", value: schema.dataHex };
}

export default function McapViewer({ fileId: propFileId }) {
  const { authFetch } = useAuth();

  // Get fileId from props or query string
  const fileId = useMemo(() => {
    if (propFileId) return propFileId;
    const params = new URLSearchParams(window.location.search);
    return params.get("fileId") || params.get("file_id");
  }, [propFileId]);

  // File and loading state
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(
    fileId ? "Loading file..." : "Upload an MCAP file or enter a URL to begin",
  );
  const [fileName, setFileName] = useState("");
  const [urlInput, setUrlInput] = useState("");

  // MCAP data
  const [header, setHeader] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [schemas, setSchemas] = useState([]);
  const [channels, setChannels] = useState([]);
  const [allMessages, setAllMessages] = useState([]);
  const [chunkIndexes, setChunkIndexes] = useState([]);
  const [attachmentIndexes, setAttachmentIndexes] = useState([]);
  const [metadataRecords, setMetadataRecords] = useState([]);

  // Playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [loop, setLoop] = useState(false);

  // Filtering
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [maxMessages, setMaxMessages] = useState(1000);

  // UI state
  const [activeTab, setActiveTab] = useState("messages");

  // Refs
  const playbackTimerRef = useRef(null);
  const allMessagesRef = useRef([]);

  // Filtered messages based on selected channels and search
  const filteredMessages = useMemo(() => {
    let msgs = allMessages;

    if (selectedChannels.length > 0) {
      msgs = msgs.filter((m) => selectedChannels.includes(m.channelId));
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      msgs = msgs.filter(
        (m) =>
          m.topic?.toLowerCase().includes(term) ||
          m.messageEncoding?.toLowerCase().includes(term) ||
          JSON.stringify(m.decoded?.value || {})
            .toLowerCase()
            .includes(term),
      );
    }

    return msgs;
  }, [allMessages, selectedChannels, searchTerm]);

  // schemaId -> schema
  const schemasById = useMemo(() => {
    const m = new Map();
    for (const s of schemas) m.set(s.id, s);
    return m;
  }, [schemas]);

  const channelsById = useMemo(() => {
    const m = new Map();
    for (const ch of channels) m.set(ch.id, ch);
    return m;
  }, [channels]);

  // Protobuf decode cache (schemaId -> {root, type})
  const protobufCache = useMemo(() => new Map(), [fileName]); // reset when new file loaded

  // Stop playback when playing state changes
  useEffect(() => {
    if (!isPlaying && playbackTimerRef.current) {
      clearInterval(playbackTimerRef.current);
      playbackTimerRef.current = null;
    }
  }, [isPlaying]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (playbackTimerRef.current) {
        clearInterval(playbackTimerRef.current);
      }
    };
  }, []);

  // Shared function to process MCAP data
  async function processMcapData(readable, name) {
    setLoading(true);
    setStatus("Loading & parsing…");
    setFileName(name);
    setHeader(null);
    setStatistics(null);
    setSchemas([]);
    setChannels([]);
    setAllMessages([]);
    setCurrentMessageIndex(0);
    setIsPlaying(false);
    setSelectedChannels([]);

    try {
      const decompressHandlers = await loadDecompressHandlers();
      const reader = await McapIndexedReader.Initialize({
        readable,
        decompressHandlers,
      });

      setHeader(reader.header ?? null);
      setStatistics(reader.statistics ?? null);

      // Chunk indexes
      const chunkList = (reader.chunkIndexes ?? []).map((ci, i) => ({
        key: i,
        messageStartTime: ci.messageStartTime?.toString() ?? "",
        messageEndTime: ci.messageEndTime?.toString() ?? "",
        compression: ci.compression || "none",
        compressedSize: ci.compressedSize?.toString() ?? "",
        uncompressedSize: ci.uncompressedSize?.toString() ?? "",
        chunkStartOffset: ci.chunkStartOffset?.toString() ?? "",
        chunkLength: ci.chunkLength?.toString() ?? "",
        channelCount: ci.messageIndexOffsets?.size ?? 0,
      }));
      setChunkIndexes(chunkList);

      // Attachment indexes
      const attachList = (reader.attachmentIndexes ?? []).map((ai, i) => ({
        key: i,
        name: ai.name ?? "",
        mediaType: ai.mediaType ?? "",
        logTime: ai.logTime?.toString() ?? "",
        createTime: ai.createTime?.toString() ?? "",
        dataSize: ai.dataSize?.toString() ?? "",
        offset: ai.offset?.toString() ?? "",
        length: ai.length?.toString() ?? "",
      }));
      setAttachmentIndexes(attachList);

      // Metadata records
      const metaList = [];
      if (typeof reader.readMetadata === "function") {
        try {
          for await (const meta of reader.readMetadata()) {
            const pairs = [];
            if (meta.metadata instanceof Map) {
              for (const [k, v] of meta.metadata.entries()) pairs.push({ key: k, value: v });
            } else if (meta.metadata && typeof meta.metadata === "object") {
              for (const [k, v] of Object.entries(meta.metadata)) pairs.push({ key: k, value: v });
            }
            metaList.push({ name: meta.name, pairs });
          }
        } catch (_) { /* metadata read is optional */ }
      }
      setMetadataRecords(metaList);

      // Schemas
      const schemaList = [];
      for (const [id, sc] of reader.schemasById.entries()) {
        schemaList.push({
          id,
          name: sc.name,
          encoding: sc.encoding,
          data: sc.data, // keep for protobuf decode
          dataSize: sc.data?.length ?? 0,
          dataHex: bytesToHex(sc.data),
        });
      }
      schemaList.sort((a, b) => a.id - b.id);
      setSchemas(schemaList);

      // Channels
      const channelList = [];
      for (const [id, ch] of reader.channelsById.entries()) {
        channelList.push({
          id,
          topic: ch.topic,
          messageEncoding: ch.messageEncoding,
          schemaId: ch.schemaId,
          metadata: ch.metadata,
        });
      }
      channelList.sort((a, b) => (a.topic || "").localeCompare(b.topic || ""));
      setChannels(channelList);

      // Messages (load all up to maxMessages)
      const msgList = [];
      let count = 0;

      for await (const msg of reader.readMessages()) {
        const ch = reader.channelsById.get(msg.channelId);
        const schema =
          ch?.schemaId != null
            ? reader.schemasById.get(ch.schemaId)
            : undefined;

        const msgEnc = (ch?.messageEncoding || "").toLowerCase();
        const schemaEnc = (schema?.encoding || "").toLowerCase();

        let decoded = null;
        let decodeError = null;

        try {
          if (msgEnc.includes("json")) {
            decoded = decodeJsonPayload(msg.data);
          } else if (
            msgEnc.includes("protobuf") &&
            schema &&
            schemaEnc === "protobuf"
          ) {
            decoded = decodeProtobufPayload({
              dataU8: msg.data,
              schema: { id: schema.id, name: schema.name, data: schema.data },
              cache: protobufCache,
            });
          } else {
            // best-effort JSON even when messageEncoding isn't explicit
            decoded = decodeJsonPayload(msg.data);
          }
        } catch (err) {
          decodeError = err?.message ?? String(err);
        }

        msgList.push({
          channelId: msg.channelId,
          topic: ch?.topic ?? `(channel ${msg.channelId})`,
          messageEncoding: ch?.messageEncoding ?? "",
          schemaId: ch?.schemaId ?? null,
          schemaName: schema?.name ?? "",
          schemaEncoding: schema?.encoding ?? "",
          logTime: msg.logTime?.toString?.() ?? String(msg.logTime),
          publishTime: msg.publishTime?.toString?.() ?? String(msg.publishTime),
          sequence: msg.sequence,
          size: msg.data?.length ?? 0,
          decoded,
          decodeError,
          hex: decoded ? null : bytesToHex(msg.data),
        });

        count++;
        if (count >= maxMessages) break;
      }

      setAllMessages(msgList);
      allMessagesRef.current = msgList;
      setStatus(`✓ Loaded ${msgList.length} messages successfully`);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setStatus(`Error: ${err?.message ?? String(err)}`);
      setLoading(false);
    }
  }

  // Playback controls
  const handlePlayPause = useCallback(() => {
    if (isPlaying) {
      setIsPlaying(false);
    } else {
      if (currentMessageIndex >= filteredMessages.length - 1) {
        setCurrentMessageIndex(0);
      }
      setIsPlaying(true);

      playbackTimerRef.current = setInterval(() => {
        setCurrentMessageIndex((prev) => {
          const next = prev + 1;
          if (next >= filteredMessages.length) {
            if (loop) {
              return 0;
            } else {
              setIsPlaying(false);
              return prev;
            }
          }
          return next;
        });
      }, 100 / playbackSpeed); // Adjust timing based on speed
    }
  }, [
    isPlaying,
    currentMessageIndex,
    filteredMessages.length,
    playbackSpeed,
    loop,
  ]);

  const handleStepForward = useCallback(() => {
    setIsPlaying(false);
    setCurrentMessageIndex((prev) =>
      Math.min(prev + 1, filteredMessages.length - 1),
    );
  }, [filteredMessages.length]);

  const handleStepBackward = useCallback(() => {
    setIsPlaying(false);
    setCurrentMessageIndex((prev) => Math.max(prev - 1, 0));
  }, []);

  const handleReset = useCallback(() => {
    setIsPlaying(false);
    setCurrentMessageIndex(0);
  }, []);

  const handleSliderChange = useCallback((value) => {
    setIsPlaying(false);
    setCurrentMessageIndex(value);
  }, []);

  async function onPickFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    await processMcapData(new BlobReadable(file), file.name);
  }

  async function onLoadUrl() {
    const url = urlInput.trim();
    if (!url) {
      setStatus("Please enter a URL");
      return;
    }

    setLoading(true);
    setStatus("Fetching from URL…");
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const blob = await response.blob();
      const filename = url.split("/").pop() || "remote.mcap";
      await processMcapData(new BlobReadable(blob), filename);
    } catch (err) {
      console.error(err);
      setStatus(`Error loading URL: ${err?.message ?? String(err)}`);
      setLoading(false);
    }
  }

  // Auto-load MCAP file from API when fileId is provided
  useEffect(() => {
    if (!fileId) return;

    async function loadFromApi() {
      setLoading(true);
      setStatus(`Loading MCAP file (ID: ${fileId})...`);

      try {
        const response = await mcapLogsService.download(authFetch, fileId);
        const blob = response.data;
        const readable = new BlobReadable(blob);
        await processMcapData(readable, `mcap-${fileId}.mcap`);
      } catch (err) {
        console.error(err);
        // 409 (FILE_ON_PRIVATE_STORAGE): no replica of the file is on
        // platform-reachable storage — playback through the server is
        // impossible; tell the user instead of showing a raw HTTP error.
        // (The body is a Blob here, so match on the status alone.)
        if (err?.response?.status === 409) {
          setStatus(
            "This file is stored on the organization's private storage and " +
              "cannot be played from here. Download it from the " +
              "organization's storage and open it as a local file instead.",
          );
        } else {
          setStatus(`Error loading file: ${err?.message ?? String(err)}`);
        }
        setLoading(false);
      }
    }

    loadFromApi();
  }, [fileId, authFetch]);

  // Current message for display
  const currentMessage = filteredMessages[currentMessageIndex];

  return (
    <div style={{ padding: "24px", background: "#f0f2f5", minHeight: "100vh" }}>
      <Card bordered={false} style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col>
            <Title level={3} style={{ margin: 0 }}>
              <DatabaseOutlined /> MCAP Player & Viewer
            </Title>
          </Col>
        </Row>

        <Divider />

        {/* File Loading Controls */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} md={8}>
            <Card size="small" hoverable>
              <Space direction="vertical" style={{ width: "100%" }}>
                <Text strong>
                  <CloudUploadOutlined /> Upload File
                </Text>
                <input
                  type="file"
                  accept=".mcap"
                  onChange={onPickFile}
                  style={{ width: "100%" }}
                />
              </Space>
            </Card>
          </Col>

          <Col xs={24} md={10}>
            <Card size="small" hoverable>
              <Space direction="vertical" style={{ width: "100%" }}>
                <Text strong>
                  <LinkOutlined /> Load from URL
                </Text>
                <Space.Compact style={{ width: "100%" }}>
                  <Input
                    placeholder="https://example.com/file.mcap"
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    onPressEnter={onLoadUrl}
                  />
                  <Button
                    type="primary"
                    onClick={onLoadUrl}
                    icon={<DownloadOutlined />}
                  >
                    Load
                  </Button>
                </Space.Compact>
              </Space>
            </Card>
          </Col>

          <Col xs={24} md={6}>
            <Card size="small">
              <Space direction="vertical" style={{ width: "100%" }}>
                <Text strong>Max Messages</Text>
                <InputNumber
                  min={100}
                  max={10000}
                  value={maxMessages}
                  onChange={(val) => setMaxMessages(val || 1000)}
                  style={{ width: "100%" }}
                />
              </Space>
            </Card>
          </Col>
        </Row>

        {/* Status */}
        <Row>
          <Col span={24}>
            <Space>
              {loading && <Spin size="small" />}
              <Text
                type={
                  status.includes("Error")
                    ? "danger"
                    : status.includes("✓")
                      ? "success"
                      : "secondary"
                }
              >
                <FileTextOutlined /> {status}
              </Text>
              {fileName && <Tag color="blue">{fileName}</Tag>}
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Statistics Cards */}
      {(header || statistics) && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          {statistics && (
            <>
              <Col xs={8} sm={4}>
                <Card>
                  <Statistic
                    title="Messages Loaded"
                    value={allMessages.length}
                    prefix={<FileTextOutlined />}
                  />
                </Card>
              </Col>

              <Col xs={8} sm={4}>
                <Card>
                  <Statistic
                    title="Total Messages"
                    value={`${statistics.messageCount?.toString() || "0"}`}
                  />
                </Card>
              </Col>              

              <Col xs={8} sm={4}>
                <Card>
                  <Statistic
                    title="Channels"
                    value={channels.length}
                    prefix={<DatabaseOutlined />}
                  />
                </Card>
              </Col>
              <Col xs={8} sm={4}>
                <Card>
                  <Statistic title="Schemas" value={schemas.length} />
                </Card>
              </Col>
              <Col xs={8} sm={4}>
                <Card>
                  <Statistic
                    title="Attachments"
                    value={statistics.attachmentsCount?.toString() || "0"}
                  />
                </Card>
              </Col>              
              <Col xs={8} sm={4}>
                <Card>
                  <Statistic
                    title="Metadata Records"
                    value={statistics.metadataCount?.toString() || "0"}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card>
                  <Space direction="vertical" size={4} style={{ width: "100%" }}>
                    <Text type="secondary">Messages Time Range</Text>
                    <Space >                      
                      <Text >
                        <Text strong>Start: </Text>
                        {nsToISOString(statistics.messageStartTime)}
                      </Text>
                      <Text >
                        <Text strong>End:&nbsp;&nbsp; </Text>
                        {nsToISOString(statistics.messageEndTime)}
                      </Text>
                    </Space>
                    <Divider style={{ margin: "6px 0" }} />
                    <Text style={{ fontSize: 13 }}>
                      <ClockCircleOutlined style={{ marginRight: 4 }} />
                      <Text strong>Duration: </Text>
                      {nsDuration(statistics.messageStartTime, statistics.messageEndTime)}
                    </Text>
                  </Space>
                </Card>
              </Col>
            </>
          )}
        </Row>
      )}

      {/* Playback Controls */}
      {allMessages.length > 0 && (
        <Card
          title={
            <Space>
              <PlayCircleOutlined />
              <span>Playback Controls</span>
              <Badge
                count={`${currentMessageIndex + 1}/${filteredMessages.length}`}
              />
            </Space>
          }
          style={{ marginBottom: 24 }}
          bordered={false}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24}>
              <Space direction="vertical" style={{ width: "100%" }}>
                <Slider
                  min={0}
                  max={Math.max(0, filteredMessages.length - 1)}
                  value={currentMessageIndex}
                  onChange={handleSliderChange}
                  tooltip={{
                    formatter: (value) =>
                      `Message ${value + 1}/${filteredMessages.length}`,
                  }}
                />
              </Space>
            </Col>

            <Col xs={24} md={12}>
              <Space wrap>
                <Tooltip title="Jump to Start">
                  <Button
                    icon={<FastBackwardOutlined />}
                    onClick={handleReset}
                  />
                </Tooltip>
                <Tooltip title="Previous Message">
                  <Button
                    icon={<StepBackwardOutlined />}
                    onClick={handleStepBackward}
                    disabled={currentMessageIndex === 0}
                  />
                </Tooltip>
                <Tooltip title={isPlaying ? "Pause" : "Play"}>
                  <Button
                    type="primary"
                    size="large"
                    icon={
                      isPlaying ? (
                        <PauseCircleOutlined />
                      ) : (
                        <PlayCircleOutlined />
                      )
                    }
                    onClick={handlePlayPause}
                  >
                    {isPlaying ? "Pause" : "Play"}
                  </Button>
                </Tooltip>
                <Tooltip title="Next Message">
                  <Button
                    icon={<StepForwardOutlined />}
                    onClick={handleStepForward}
                    disabled={
                      currentMessageIndex >= filteredMessages.length - 1
                    }
                  />
                </Tooltip>
                <Tooltip title="Loop">
                  <Button
                    icon={<ReloadOutlined />}
                    type={loop ? "primary" : "default"}
                    onClick={() => setLoop(!loop)}
                  >
                    Loop
                  </Button>
                </Tooltip>
              </Space>
            </Col>

            <Col xs={24} md={12}>
              <Space wrap style={{ float: "right" }}>
                <Text>Speed:</Text>
                <Select
                  value={playbackSpeed}
                  onChange={setPlaybackSpeed}
                  style={{ width: 100 }}
                >
                  <Option value={0.25}>0.25x</Option>
                  <Option value={0.5}>0.5x</Option>
                  <Option value={1}>1x</Option>
                  <Option value={2}>2x</Option>
                  <Option value={5}>5x</Option>
                  <Option value={10}>10x</Option>
                </Select>

                <Select
                  mode="multiple"
                  placeholder="Filter by channel"
                  value={selectedChannels}
                  onChange={setSelectedChannels}
                  style={{ minWidth: 200 }}
                  maxTagCount="responsive"
                >
                  {channels.map((ch) => (
                    <Option key={ch.id} value={ch.id}>
                      {ch.topic}
                    </Option>
                  ))}
                </Select>

                <Input
                  placeholder="Search messages..."
                  prefix={<SearchOutlined />}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ width: 200 }}
                  allowClear
                />
              </Space>
            </Col>
          </Row>
        </Card>
      )}

      {/* Main Content Tabs */}
      {allMessages.length > 0 && (
        <Card bordered={false}>
          <Tabs activeKey={activeTab} onChange={setActiveTab}>
            {/* Messages Tab */}
            <TabPane
              tab={`Messages (${filteredMessages.length})`}
              key="messages"
            >
              {currentMessage ? (
                <Card
                  title={
                    <Space>
                      <Tag color="blue">Message {currentMessageIndex + 1}</Tag>
                      <Text strong>{currentMessage.topic}</Text>
                      <Tag>{currentMessage.messageEncoding || "unknown"}</Tag>
                    </Space>
                  }
                  extra={
                    <Space>
                      <Text type="secondary">
                        Channel {currentMessage.channelId}
                      </Text>
                      <Text type="secondary">
                        Seq: {currentMessage.sequence ?? "—"}
                      </Text>
                      <Tag>{currentMessage.size} bytes</Tag>
                    </Space>
                  }
                >
                  <Row gutter={[16, 16]}>
                    <Col xs={24} md={12}>
                      <Space direction="vertical" size="small">
                        <Text type="secondary">
                          Log Time: {currentMessage.logTime}
                        </Text>
                        <Text type="secondary">
                          Publish Time: {currentMessage.publishTime}
                        </Text>
                        <Text type="secondary">
                          Schema: {currentMessage.schemaName || "—"}
                        </Text>
                      </Space>
                    </Col>
                  </Row>

                  <Divider />

                  {currentMessage.decodeError && (
                    <Card
                      size="small"
                      style={{
                        marginBottom: 16,
                        background: "#fff2e8",
                        border: "1px solid #ffbb96",
                      }}
                    >
                      <Text type="danger">
                        Decode error: {currentMessage.decodeError}
                      </Text>
                    </Card>
                  )}

                  {currentMessage.decoded ? (
                    <Card
                      size="small"
                      title="Message Data"
                      style={{ background: "#f6f6f6" }}
                    >
                      <pre
                        style={{
                          margin: 0,
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          maxHeight: 500,
                          overflow: "auto",
                        }}
                      >
                        {JSON.stringify(currentMessage.decoded.value, null, 2)}
                      </pre>
                    </Card>
                  ) : (
                    <Card
                      size="small"
                      title="Raw Data (Hex)"
                      style={{ background: "#f6f6f6" }}
                    >
                      <Text code style={{ wordBreak: "break-all" }}>
                        {currentMessage.hex}
                      </Text>
                    </Card>
                  )}
                </Card>
              ) : (
                <Empty description="No messages to display" />
              )}
            </TabPane>

            {/* All Messages List Tab */}
            <TabPane tab="All Messages List" key="list">
              <Table
                dataSource={filteredMessages}
                rowKey={(record, index) => index}
                pagination={{ pageSize: 50 }}
                scroll={{ x: true }}
                size="small"
                columns={[
                  {
                    title: "#",
                    dataIndex: "index",
                    key: "index",
                    width: 60,
                    render: (_, __, index) => index + 1,
                  },
                  {
                    title: "Topic",
                    dataIndex: "topic",
                    key: "topic",
                    ellipsis: true,
                  },
                  {
                    title: "Channel",
                    dataIndex: "channelId",
                    key: "channelId",
                    width: 80,
                  },
                  {
                    title: "Encoding",
                    dataIndex: "messageEncoding",
                    key: "messageEncoding",
                    width: 120,
                  },
                  {
                    title: "Size",
                    dataIndex: "size",
                    key: "size",
                    width: 80,
                    render: (size) => `${size} B`,
                  },
                  {
                    title: "Sequence",
                    dataIndex: "sequence",
                    key: "sequence",
                    width: 80,
                  },
                  {
                    title: "Actions",
                    key: "actions",
                    width: 100,
                    render: (_, __, index) => (
                      <Button
                        size="small"
                        onClick={() => {
                          setCurrentMessageIndex(index);
                          setActiveTab("messages");
                        }}
                      >
                        View
                      </Button>
                    ),
                  },
                ]}
              />
            </TabPane>

            {/* Metadata Tab */}
            <TabPane tab={<Space><TagsOutlined />MCAP Metadata {`(${metadataRecords.length})`}</Space>} key="metadata">
              {metadataRecords.length > 0 ? (
                <Collapse ghost>
                  {metadataRecords.map((rec, i) => (
                    <Collapse.Panel
                      key={i}
                      header={<Text strong> {`${i + 1}) ${rec.name || `(record ${i + 1})`}`}</Text>}
                    >
                      {rec.pairs.length > 0 ? (
                        <Table
                          size="small"
                          pagination={false}
                          dataSource={rec.pairs.map((p, j) => ({ ...p, index: j, key: p.key || `key_${j}`, value: p.value ?? "\u2014"}))}
                          columns={[
                            { title: "Id", dataIndex: "index", key: "index", width: "1cm" },
                            { title: "Key", dataIndex: "key", key: "key", width: "20%" },
                            { title: "Value", dataIndex: "value", key: "value" },
                          ]}
                        />
                      ) : (
                        <Text type="secondary">No key-value pairs</Text>
                      )}
                    </Collapse.Panel>
                  ))}
                </Collapse>                
              ) : (
                <Empty description="No metadata records found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
            </TabPane>

            {/* Channels Tab */}
            <TabPane tab={`Channels (${channels.length})`} key="channels">
              <Table
                dataSource={channels}
                rowKey="id"
                pagination={false}
                size="small"
                expandable={{
                  expandedRowRender: (ch) => {
                    const meta = ch.metadata instanceof Map
                      ? Array.from(ch.metadata.entries())
                      : Object.entries(ch.metadata || {});
                    const schema = schemas.find(s => s.id === ch.schemaId);
                    return (
                      <Row gutter={[16, 12]} style={{ padding: "8px 0" }}>
                        <Col xs={24} md={12}>
                          <Text strong>Schema</Text>
                          <Descriptions size="small" column={1} bordered style={{ marginTop: 6 }}>
                            <Descriptions.Item label="ID">{schema?.id ?? "\u2014"}</Descriptions.Item>
                            <Descriptions.Item label="Name">{schema?.name ?? "\u2014"}</Descriptions.Item>
                            <Descriptions.Item label="Encoding">{schema?.encoding ?? "\u2014"}</Descriptions.Item>
                            <Descriptions.Item label="Size">{schema ? `${schema.dataSize} bytes` : "\u2014"}</Descriptions.Item>
                          </Descriptions>
                        </Col>
                        {meta.length > 0 && (
                          <Col xs={24} md={12}>
                            <Text strong>Channel Metadata</Text>
                            <Table
                              size="small"
                              pagination={false}
                              style={{ marginTop: 6 }}
                              dataSource={meta.map(([k, v], j) => ({ key: j, k, v }))}
                              columns={[
                                { title: "Key", dataIndex: "k", key: "k", width: "40%" },
                                { title: "Value", dataIndex: "v", key: "v" },
                              ]}
                            />
                          </Col>
                        )}
                      </Row>
                    );
                  },
                }}
                columns={[
                  {
                    title: "ID",
                    dataIndex: "id",
                    key: "id",
                    width: 60,
                  },
                  {
                    title: "Topic",
                    dataIndex: "topic",
                    key: "topic",
                    ellipsis: true,
                  },
                  {
                    title: "Message Encoding",
                    dataIndex: "messageEncoding",
                    key: "messageEncoding",
                  },
                  {
                    title: "Schema ID",
                    dataIndex: "schemaId",
                    key: "schemaId",
                    width: 100,
                  },
                  {
                    title: "Schema Name",
                    key: "schemaName",
                    ellipsis: true,
                    render: (_, ch) => schemas.find(s => s.id === ch.schemaId)?.name ?? "\u2014",
                  },
                ]}
              />
            </TabPane>

            {/* Schemas Tab */}
            <TabPane tab={`Schemas (${schemas.length})`} key="schemas">
              <Table
                dataSource={schemas}
                rowKey="id"
                pagination={false}
                size="small"
                expandable={{
                  expandedRowRender: (record) => {
                    const decoded = decodeSchemaData(record);
                    const enc = (record.encoding || "").toLowerCase();

                    const kindLabel = {
                      json: "JSON Schema",
                      text: "Text",
                      protobuf_fds: "Protobuf FileDescriptorSet",
                      hex: "Raw Hex",
                      error: "Decode Error",
                    };

                    return (
                      <Card size="small" style={{ background: "#fafafa" }}>
                        {decoded && decoded.kind !== "hex" ? (
                          <>
                            <Space style={{ marginBottom: 8 }}>
                              <Text strong>Decoded Schema</Text>
                              <Tag color={decoded.kind === "error" ? "red" : "green"}>
                                {kindLabel[decoded.kind] ?? decoded.kind}
                              </Tag>
                            </Space>

                            {decoded.kind === "error" ? (
                              <Text type="danger">{decoded.value}</Text>
                            ) : (
                              <pre
                                style={{
                                  margin: 0,
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-word",
                                  maxHeight: 400,
                                  overflow: "auto",
                                  background: "#f0f0f0",
                                  padding: "8px",
                                  borderRadius: 4,
                                  fontSize: 12,
                                }}
                              >
                                {decoded.kind === "json" || decoded.kind === "protobuf_fds"
                                  ? JSON.stringify(decoded.value, null, 2)
                                  : decoded.value}
                              </pre>
                            )}

                            <Divider style={{ margin: "12px 0" }} />
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              Raw Hex: <Text code style={{ fontSize: 11 }}>{record.dataHex}</Text>
                            </Text>
                          </>
                        ) : (
                          <>
                            <Space style={{ marginBottom: 8 }}>
                              <Text strong>Data Preview (Hex)</Text>
                              {enc && <Tag>{enc}</Tag>}
                            </Space>
                            <Paragraph code style={{ marginTop: 8, fontSize: 12 }}>
                              {record.dataHex}
                            </Paragraph>
                          </>
                        )}
                      </Card>
                    );
                  },
                }}
                columns={[
                  {
                    title: "ID",
                    dataIndex: "id",
                    key: "id",
                    width: 60,
                  },
                  {
                    title: "Name",
                    dataIndex: "name",
                    key: "name",
                    ellipsis: true,
                  },
                  {
                    title: "Encoding",
                    dataIndex: "encoding",
                    key: "encoding",
                  },
                  {
                    title: "Size",
                    dataIndex: "dataSize",
                    key: "dataSize",
                    width: 100,
                    render: (size) => `${size} bytes`,
                  },
                ]}
              />
            </TabPane>

            {/* Attachment Indexes Tab */}
            <TabPane tab={<Space><PaperClipOutlined />Attachments {`(${attachmentIndexes.length})`}</Space>} key="attachment-indexes">
              {attachmentIndexes.length > 0 ? (
                <Table
                  size="small"
                  dataSource={attachmentIndexes}
                  pagination={{ pageSize: 20, size: "small" }}
                  scroll={{ x: true }}
                  columns={[
                    { title: "Name", dataIndex: "name", key: "name", ellipsis: true },
                    { title: "Media Type", dataIndex: "mediaType", key: "mediaType" },
                    { title: "Data Size (B)", dataIndex: "dataSize", key: "dataSize" },
                    { title: "Log Time (ns)", dataIndex: "logTime", key: "logTime", ellipsis: true },
                    { title: "Create Time (ns)", dataIndex: "createTime", key: "createTime", ellipsis: true },
                  ]}
                />
              ) : (
                <Empty description="No attachment indexes found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
            </TabPane>

            {/* Chunk Indexes Tab */}
            <TabPane tab={<Space><CompressOutlined />Chunk Indexes {`(${chunkIndexes.length})`}</Space>} key="chunk-indexes">
              {chunkIndexes.length > 0 ? (
                <Table
                  size="small"
                  dataSource={chunkIndexes}
                  pagination={{ pageSize: 20, size: "small" }}
                  scroll={{ x: true }}
                  columns={[
                    { title: "#", key: "i", render: (_, __, i) => i + 1, width: 50 },
                    { title: "Compression", dataIndex: "compression", key: "compression", width: 100 },
                    { title: "Channels", dataIndex: "channelCount", key: "channelCount", width: 80 },
                    { title: "Compressed (B)", dataIndex: "compressedSize", key: "compressedSize" },
                    { title: "Uncompressed (B)", dataIndex: "uncompressedSize", key: "uncompressedSize" },
                    { title: "Start Time (ns)", dataIndex: "messageStartTime", key: "messageStartTime", ellipsis: true },
                    { title: "End Time (ns)", dataIndex: "messageEndTime", key: "messageEndTime", ellipsis: true },
                  ]}
                />
              ) : (
                <Empty description="No chunk indexes found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
            </TabPane>

            {/* File Header Tab */}
            <TabPane
              tab={<Space><FileTextOutlined />File Header </Space>}
              key="file-header"
            >
              {header ? (
                <Card size="small">
                  <Descriptions column={1} size="small" bordered>
                    {Object.entries(header).map(([k, v]) => (
                      <Descriptions.Item label={k} key={k}>
                        <Text code>{typeof v === "bigint" ? v.toString() : String(v ?? "—")}</Text>
                      </Descriptions.Item>
                    ))}
                  </Descriptions>
                </Card>
              ) : (
                <Empty description="No file header found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
            </TabPane>

          </Tabs>
        </Card>
      )}

      {allMessages.length === 0 && !loading && (
        <Card>
          <Empty
            description="No MCAP data loaded. Upload a file or enter a URL to get started."
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </Card>
      )}
    </div>
  );
}
